import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, ChevronRight, Send, Bell, Zap, ArrowRight
} from 'lucide-react';

const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function CommsOverview() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    emailConnected: false,
    smsConnected: false,
    activeNotifications: 0,
    messagesSent7Days: 0,
    failures7Days: 0
  });

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadStats();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [emailRes, smsRes, triggersRes, logsRes] = await Promise.all([
        axios.get(`${API}/platform/email-config`, getAuthConfig()).catch(() => ({ data: null })),
        axios.get(`${API}/platform/sms-config`, getAuthConfig()).catch(() => ({ data: null })),
        axios.get(`${API}/platform/email-triggers`, getAuthConfig()).catch(() => ({ data: [] })),
        axios.get(`${API}/platform/email-logs?days=7`, getAuthConfig()).catch(() => ({ data: { sent: 0, failed: 0 } }))
      ]);

      // Count active notifications (triggers with email or SMS enabled)
      const triggers = triggersRes.data || [];
      const activeCount = triggers.filter(t => 
        (t.email_enabled || t.is_enabled) || t.sms_enabled
      ).length;

      setStats({
        emailConnected: emailRes.data?.is_configured || false,
        smsConnected: smsRes.data?.enabled || false,
        activeNotifications: activeCount,
        messagesSent7Days: logsRes.data?.sent || 0,
        failures7Days: logsRes.data?.failed || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const ChannelStatus = ({ name, icon: Icon, connected, href }) => (
    <Link to={href}>
      <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                connected ? 'bg-emerald-500/10' : 'bg-zinc-800'
              }`}>
                <Icon className={`w-5 h-5 ${connected ? 'text-emerald-400' : 'text-zinc-500'}`} />
              </div>
              <div>
                <p className="font-medium text-zinc-100 text-sm">{name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {connected ? (
                    <>
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Connected</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3 text-zinc-500" />
                      <span className="text-xs text-zinc-500">Not configured</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-600" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="comms-overview">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Communications</h1>
            <p className="text-sm text-zinc-500">Manage notifications, templates, and delivery channels</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadStats}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Channel Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChannelStatus 
            name="Email" 
            icon={Mail} 
            connected={stats.emailConnected} 
            href="/super-admin/comms/channels"
          />
          <ChannelStatus 
            name="SMS" 
            icon={MessageSquare} 
            connected={stats.smsConnected} 
            href="/super-admin/comms/channels"
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Active</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">{stats.activeNotifications}</p>
                  <p className="text-xs text-zinc-500 mt-1">notifications</p>
                </div>
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Sent</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">{stats.messagesSent7Days}</p>
                  <p className="text-xs text-zinc-500 mt-1">last 7 days</p>
                </div>
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Failed</p>
                  <p className={`text-2xl font-bold mt-1 ${stats.failures7Days > 0 ? 'text-red-400' : 'text-zinc-100'}`}>
                    {stats.failures7Days}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">last 7 days</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stats.failures7Days > 0 ? 'bg-red-500/10' : 'bg-zinc-800'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${stats.failures7Days > 0 ? 'text-red-400' : 'text-zinc-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-zinc-100">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/super-admin/comms/notifications">
                <Button 
                  variant="outline" 
                  className="w-full justify-between border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
                  data-testid="view-notifications-btn"
                >
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    View Notifications
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/super-admin/comms/channels">
                <Button 
                  variant="outline" 
                  className="w-full justify-between border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
                  data-testid="test-channels-btn"
                >
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Test Channels
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Channel Blocked Warning */}
        {(!stats.emailConnected || !stats.smsConnected) && (
          <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-400">Channel not configured</p>
              <p className="text-xs text-amber-400/70 mt-1">
                {!stats.emailConnected && !stats.smsConnected 
                  ? 'Email and SMS channels are not configured. Notifications will not be sent.'
                  : !stats.emailConnected 
                    ? 'Email channel is not configured. Email notifications will not be sent.'
                    : 'SMS channel is not configured. SMS notifications will not be sent.'
                }
              </p>
              <Link to="/super-admin/comms/channels">
                <Button size="sm" className="mt-3 bg-amber-500 hover:bg-amber-600 text-zinc-900">
                  Configure Channels
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}
